import { Spinner } from '@/components/glitch-ui/spinner';
import { useState } from 'react';
import { Settings2, AlertCircle, Lightbulb } from 'lucide-react';
import { CampaignBudgetInput } from '../../glitch-ui/campaign-budget-input';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { toast } from '@/hooks/useToast';

interface CampaignBudgetViewProps {
  budget: number;
  setBudget: (budget: number) => void;
  isBudgetApproved?: boolean;
  onManageBudget?: () => void;
  /** When provided, shows a "Get recommendation" link that calls this and prefills/suggests a budget. */
  onGetRecommendation?: () => Promise<number>;
  /** Remaining daily budget available within platform allocation. Null = no platform budgets set. */
  remainingDailyBudget?: number | null;
  /** Original saved budget for learning phase warning. */
  savedBudget?: number;
  disabled?: boolean;
  /** Whether the campaign has been live before. If false, learning phase warning is hidden. */
  hasBeenLive?: boolean;
}

export const CampaignBudgetView: React.FC<CampaignBudgetViewProps> = ({
  budget,
  setBudget,
  isBudgetApproved = false,
  onManageBudget,
  onGetRecommendation,
  remainingDailyBudget,
  savedBudget,
  disabled = false,
  hasBeenLive = true,
}) => {
  const { symbol } = useCurrencySymbol();
  const cs = symbol ?? '';
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
  const [recommendation, setRecommendation] = useState<number | null>(null);

  const handleGetRecommendation = async () => {
    if (!onGetRecommendation) return;
    setIsLoadingRecommendation(true);
    try {
      const recommended = await onGetRecommendation();
      setRecommendation(recommended);
      if (budget === 0) {
        setBudget(recommended);
      }
    } catch (err) {
      toast({
        title: 'Could not get recommendation',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingRecommendation(false);
    }
  };

  const handleBudgetChange = (newBudget: number) => {
    setBudget(newBudget);
  };

  const isOverAllocation = remainingDailyBudget != null && budget > remainingDailyBudget;
  const showLearningWarning = hasBeenLive && savedBudget != null && savedBudget > 0 && budget > 0 &&
    Math.abs(budget - savedBudget) / savedBudget >= 0.2;

  return (
    <div>
      <div className='mb-4'>
        <h2 className='text-xl font-semibold text-gray-900'>Daily Budget</h2>
        <p className='text-sm text-gray-600 mt-1'>
          {isBudgetApproved
            ? 'Your daily budget for this campaign.'
            : 'Set your daily budget. Approval is required before going live.'}
        </p>
      </div>
      <div className='flex items-center gap-3'>
        <div className='w-[120px]'>
          <CampaignBudgetInput
            value={budget}
            setValue={handleBudgetChange}
            placeholder='0'
            cycle='day'
            disabled={disabled}
          />
        </div>
        {!disabled && (onManageBudget || onGetRecommendation) && (
          <div className="flex flex-col gap-2">
            {onManageBudget && (
              <div className="flex items-center gap-1.5">
                <button
                  data-testid="campaign-edit-budget-cta"
                  onClick={onManageBudget}
                  className="flex items-center gap-1.5 text-sm text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors cursor-pointer"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  Manage Budget
                </button>
                {remainingDailyBudget != null && !isOverAllocation && (
                  <span className="text-sm text-gray-500">
                    ({cs}{(remainingDailyBudget - budget).toLocaleString()}/day available)
                  </span>
                )}
              </div>
            )}
            {onGetRecommendation && (
              <button
                onClick={handleGetRecommendation}
                disabled={isLoadingRecommendation}
                className="flex items-center gap-1.5 text-sm text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors cursor-pointer disabled:opacity-50"
              >
                {isLoadingRecommendation
                  ? <Spinner size="sm" />
                  : <Lightbulb className="h-3.5 w-3.5" />}
                {isLoadingRecommendation
                  ? 'Getting recommendation...'
                  : recommendation !== null
                    ? `Recommended: ${cs}${recommendation}/day`
                    : 'Get recommendation'}
              </button>
            )}
          </div>
        )}
      </div>
      {showLearningWarning && (
        <div className="mt-3 flex items-start gap-2 text-sm text-amber-600">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>A change of this size may retrigger the campaign learning phase. Smaller incremental adjustments are recommended.</span>
        </div>
      )}
      {isOverAllocation && (
        <div className="mt-3 flex items-start gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Exceeds platform allocation. Max available: {cs}{Math.max(0, remainingDailyBudget!).toLocaleString()}/day.{' '}
            {onManageBudget ? (
              <>
                Click{' '}
                <button
                  onClick={onManageBudget}
                  className="font-medium underline text-red-600 hover:text-red-700 cursor-pointer"
                >
                  Manage Budget
                </button>{' '}
                to adjust your platform budgets.
              </>
            ) : (
              'Adjust your platform budgets under Business → Budget.'
            )}
          </span>
        </div>
      )}
    </div>
  );
};
